export interface VisiblePillCountInput {
  /** Border-box width of each pill, in order. */
  pillWidths: readonly number[];
  /** Space available for the pills and, when they overflow, the "+N" trigger. */
  containerWidth: number;
  /** Width of the "+N" trigger, including the gap before it. Spent only when the pills overflow. */
  reservedWidth: number;
  gap: number;
}

/**
 * How many leading pills fit on one line.
 * A row that already fits stays fully visible — `reservedWidth` is only spent
 * once something would wrap, so the trigger does not hide a pill that fits.
 * Always keeps one pill when the list is non-empty, even if that pill is wider
 * than the container.
 */
export function getVisiblePillCount({
  pillWidths,
  containerWidth,
  reservedWidth,
  gap,
}: VisiblePillCountInput): number {
  const count = pillWidths.length;
  if (count === 0) return 0;

  const widthOf = (n: number): number => {
    let total = 0;
    for (let i = 0; i < n; i += 1) {
      total += pillWidths[i] ?? 0;
      if (i > 0) total += gap;
    }
    return total;
  };

  if (widthOf(count) <= containerWidth) return count;

  const budget = containerWidth - reservedWidth;
  let visible = 1;
  let used = pillWidths[0] ?? 0;
  for (let i = 1; i < count; i += 1) {
    const next = used + gap + (pillWidths[i] ?? 0);
    if (next > budget) break;
    used = next;
    visible = i + 1;
  }
  return visible;
}
